import React, { useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Calendar, List, User, Building, Clock, Sun, Moon, AlertTriangle, TrendingDown, ExternalLink } from 'lucide-react';
interface Shift {
  date: Date;
  site: string;
  startTime: string;
  endTime: string;
  isOvertime: boolean;
  isNightShift: boolean;
}
interface GuardRosterModalProps {
  isOpen: boolean;
  guardName: string;
  guardId: string;
  grade: string;
  primarySite: string;
  totalShifts: number;
  variant?: 'over-threshold' | 'under-utilised';
  onClose: () => void;
  onTakeAction?: () => void;
}
// Generate mock shifts for a guard
const generateMockShifts = (guardId: string, year: number, month: number, totalShifts: number): Shift[] => {
  const shifts: Shift[] = [];
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const sites = ['Woolworths HQ', 'Standard Bank Tower', 'Pick n Pay Constantia', 'V&A Waterfront', 'Sandton City Mall'];
  // Seed based on guardId for consistent results
  const seed = guardId.charCodeAt(0) || 1;
  let shiftCount = 0;
  // First pass: distribute shifts more evenly across the month
  // We need to ensure we generate exactly totalShifts shifts
  const daysToWork: number[] = [];
  // Calculate which days to work to reach totalShifts
  for (let day = 1; day <= daysInMonth && daysToWork.length < totalShifts; day++) {
    const dayOfWeek = new Date(year, month, day).getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    // Work pattern: most weekdays, some weekends
    // Adjust pattern to ensure we hit the target shift count
    if (!isWeekend) {
      // Work most weekdays
      if ((day + seed) % 4 !== 0 || daysToWork.length < totalShifts - 5) {
        daysToWork.push(day);
      }
    } else {
      // Work some weekends, especially if we need more shifts
      if ((day + seed) % 2 === 0 || daysToWork.length < totalShifts - 3) {
        daysToWork.push(day);
      }
    }
  }
  // If we still don't have enough shifts, fill in remaining days
  if (daysToWork.length < totalShifts) {
    for (let day = 1; day <= daysInMonth && daysToWork.length < totalShifts; day++) {
      if (!daysToWork.includes(day)) {
        daysToWork.push(day);
      }
    }
  }
  // Sort days and take only what we need
  daysToWork.sort((a, b) => a - b);
  const finalDays = daysToWork.slice(0, totalShifts);
  // Generate shifts for the selected days
  finalDays.forEach((day, index) => {
    shiftCount++;
    const isNightShift = (day + seed) % 3 === 0;
    const siteIndex = (day + seed) % sites.length;
    shifts.push({
      date: new Date(year, month, day),
      site: sites[siteIndex],
      startTime: isNightShift ? '18:00' : '06:00',
      endTime: isNightShift ? '06:00' : '18:00',
      isOvertime: shiftCount > 19,
      isNightShift
    });
  });
  return shifts;
};
const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month + 1, 0).getDate();
};
const getMonthName = (month: number): string => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return months[month];
};
const getDayName = (day: number): string => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return days[day];
};
const getFirstDayOfMonth = (year: number, month: number): number => {
  return new Date(year, month, 1).getDay();
};
export function GuardRosterModal({
  isOpen,
  guardName,
  guardId,
  grade,
  primarySite,
  totalShifts,
  variant = 'under-utilised',
  onClose,
  onTakeAction
}: GuardRosterModalProps) {
  const [currentDate, setCurrentDate] = useState(new Date(2026, 1, 1)); // February 2026
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [shifts, setShifts] = useState<Shift[]>([]);
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDayOfMonth = getFirstDayOfMonth(year, month);
  const isOverThreshold = variant === 'over-threshold';
  // Generate shifts when modal opens or month changes
  useEffect(() => {
    if (isOpen) {
      setShifts(generateMockShifts(guardId, year, month, totalShifts));
    }
  }, [isOpen, guardId, year, month, totalShifts]);
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };
  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };
  // Calculate stats
  const overtimeShifts = shifts.filter(s => s.isOvertime).length;
  const totalHours = shifts.length * 12;
  const overtimeHours = overtimeShifts * 12;
  const availableShifts = Math.max(0, 19 - shifts.length);
  const shiftsOverThreshold = Math.max(0, shifts.length - 19);
  // Get shift for a specific day
  const getShiftForDay = (day: number): Shift | undefined => {
    return shifts.find(s => s.date.getDate() === day);
  };
  const getGradeBadgeColor = (gradeStr: string): string => {
    if (gradeStr.includes('A')) return 'bg-emerald-100 text-emerald-700';
    if (gradeStr.includes('B')) return 'bg-sky-100 text-sky-700';
    return 'bg-amber-100 text-amber-700';
  };
  if (!isOpen) return null;
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-[#062133]/40 backdrop-blur-sm z-40 transition-opacity duration-300" onClick={onClose} aria-hidden="true" data-id="element-573" />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="guard-roster-modal-title" data-id="element-574">
        <div className="bg-white rounded-3xl shadow-2xl w-[90vw] max-w-[900px] max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()} data-id="element-575">
          {/* Header - clean white background for both variants */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-[#e5e5e5] flex-shrink-0" data-id="element-576">
            <div className="flex items-center gap-3" data-id="element-577">
              <div className="h-10 w-10 rounded-full bg-[#062133]/5 flex items-center justify-center" data-id="element-578">
                <User className="h-5 w-5 text-[#062133]" data-id="element-579" />
              </div>
              <div data-id="element-580">
                <div className="flex items-center gap-2" data-id="element-581">
                  <h2 id="guard-roster-modal-title" className="text-xl font-bold text-[#062133]" data-id="element-582">
                    {guardName}
                  </h2>
                  {isOverThreshold && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-rose-500 text-white" data-id="element-583">
                      Over Threshold
                    </span>}
                </div>
                <div className="flex items-center gap-2 mt-0.5" data-id="element-584">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getGradeBadgeColor(grade)}`} data-id="element-585">
                    {grade}
                  </span>
                  {isOverThreshold && <span className="text-xs text-rose-600 font-medium" data-id="element-586">
                      +{shiftsOverThreshold} shifts over limit
                    </span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-587">
              <button onClick={() => {
              onClose();
              onTakeAction?.();
            }} className="inline-flex items-center gap-2 px-4 py-2 bg-[#ff697f] hover:bg-[#e65a6f] text-white text-sm font-medium rounded-xl transition-colors" data-id="element-588">
                <ExternalLink className="h-4 w-4" data-id="element-589" />
                Take Action
              </button>
              <button onClick={onClose} className="p-2 text-[#5a7280] hover:text-[#062133] hover:bg-[#fafaf9] rounded-lg transition-colors" aria-label="Close modal" data-id="element-590">
                <X className="h-5 w-5" data-id="element-591" />
              </button>
            </div>
          </div>

          {/* Month Navigation */}
          <div className="flex items-center justify-between px-6 py-3 border-b border-[#e5e5e5] bg-[#fafaf9] flex-shrink-0" data-id="element-592">
            <div className="flex items-center gap-4" data-id="element-593">
              <button onClick={handlePrevMonth} className="p-2 text-[#5a7280] hover:text-[#062133] hover:bg-white rounded-lg transition-colors" aria-label="Previous month" data-id="element-594">
                <ChevronLeft className="h-5 w-5" data-id="element-595" />
              </button>
              <span className="text-lg font-semibold text-[#062133] min-w-[160px] text-center" data-id="element-596">
                {getMonthName(month)} {year}
              </span>
              <button onClick={handleNextMonth} className="p-2 text-[#5a7280] hover:text-[#062133] hover:bg-white rounded-lg transition-colors" aria-label="Next month" data-id="element-597">
                <ChevronRight className="h-5 w-5" data-id="element-598" />
              </button>
            </div>

            {/* View Toggle */}
            <div className="flex items-center bg-white rounded-lg border border-[#e5e5e5] p-1" data-id="element-599">
              <button onClick={() => setViewMode('calendar')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'calendar' ? 'bg-[#062133] text-white' : 'text-[#5a7280] hover:text-[#062133]'}`} data-id="element-600">
                <Calendar className="h-4 w-4" data-id="element-601" />
                Calendar
              </button>
              <button onClick={() => setViewMode('list')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-[#062133] text-white' : 'text-[#5a7280] hover:text-[#062133]'}`} data-id="element-602">
                <List className="h-4 w-4" data-id="element-603" />
                List
              </button>
            </div>
          </div>

          {/* Guard Summary - variant-specific stats */}
          <div className="px-6 py-3 border-b border-[#e5e5e5] flex-shrink-0" data-id="element-604">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-605">
              {/* Total Shifts - highlighted differently based on variant */}
              <div className={`flex items-center gap-3 p-3 rounded-xl ${isOverThreshold ? 'bg-rose-50 border border-rose-200' : 'bg-[#fafaf9]'}`} data-id="element-606">
                <Calendar className={`h-5 w-5 ${isOverThreshold ? 'text-rose-500' : 'text-[#5a7280]'}`} data-id="element-607" />
                <div data-id="element-608">
                  <p className="text-xs text-[#5a7280]" data-id="element-609">Total Shifts</p>
                  <p className={`text-lg font-bold ${isOverThreshold ? 'text-rose-600' : shifts.length < 19 ? 'text-sky-600' : 'text-[#062133]'}`} data-id="element-610">
                    {shifts.length}
                  </p>
                </div>
              </div>

              {/* Variant-specific second stat */}
              {isOverThreshold ? <div className="flex items-center gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl" data-id="element-611">
                  <AlertTriangle className="h-5 w-5 text-rose-500" data-id="element-612" />
                  <div data-id="element-613">
                    <p className="text-xs text-[#5a7280]" data-id="element-614">Overtime Shifts</p>
                    <p className="text-lg font-bold text-rose-600" data-id="element-615">
                      {overtimeShifts}
                    </p>
                  </div>
                </div> : <div className="flex items-center gap-3 p-3 bg-sky-50 border border-sky-200 rounded-xl" data-id="element-616">
                  <TrendingDown className="h-5 w-5 text-sky-500" data-id="element-617" />
                  <div data-id="element-618">
                    <p className="text-xs text-[#5a7280]" data-id="element-619">Available Capacity</p>
                    <p className="text-lg font-bold text-sky-600" data-id="element-620">
                      +{availableShifts}
                    </p>
                  </div>
                </div>}

              <div className="flex items-center gap-3 p-3 bg-[#fafaf9] rounded-xl" data-id="element-621">
                <Building className="h-5 w-5 text-[#5a7280]" data-id="element-622" />
                <div data-id="element-623">
                  <p className="text-xs text-[#5a7280]" data-id="element-624">Primary Site</p>
                  <p className="text-sm font-semibold text-[#062133] truncate" data-id="element-625">
                    {primarySite}
                  </p>
                </div>
              </div>
            </div>

            {/* Warning banner for over-threshold */}
            {isOverThreshold && <div className="mt-3 p-3 bg-rose-100 border border-rose-200 rounded-xl flex items-center gap-3" data-id="element-626">
                <AlertTriangle className="h-5 w-5 text-rose-600 flex-shrink-0" data-id="element-627" />
                <div data-id="element-628">
                  <p className="text-sm font-medium text-rose-800" data-id="element-629">
                    This guard has exceeded the 19-shift monthly threshold
                  </p>
                  <p className="text-xs text-rose-600 mt-0.5" data-id="element-630">
                    {overtimeShifts} overtime shifts totaling {overtimeHours}{' '}
                    hours of overtime pay
                  </p>
                </div>
              </div>}
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-auto p-4" data-id="element-631">
            {viewMode === 'calendar' /* Calendar View - Tighter layout */ ? <div data-id="element-632">
                {/* Day Headers */}
                <div className="grid grid-cols-7 gap-1 mb-1" data-id="element-633">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className="text-center text-xs font-semibold text-[#5a7280] uppercase tracking-wider py-1" data-id="element-634">
                        {day}
                      </div>)}
                </div>

                {/* Calendar Grid - Tighter cells */}
                <div className="grid grid-cols-7 gap-1" data-id="element-635">
                  {/* Empty cells for days before the first of the month */}
                  {Array.from({
                length: firstDayOfMonth
              }).map((_, index) => <div key={`empty-${index}`} className="h-16 rounded-lg" data-id="element-636" />)}

                  {/* Day cells */}
                  {Array.from({
                length: daysInMonth
              }, (_, i) => i + 1).map(day => {
                const shift = getShiftForDay(day);
                const isToday = day === 15; // Mock "today" for demo
                return <div key={day} className={`h-16 rounded-lg border p-1.5 transition-colors ${shift ? shift.isOvertime ? 'bg-rose-100 border-rose-300' : isOverThreshold ? 'bg-emerald-50 border-emerald-200' : 'bg-emerald-50 border-emerald-200' : 'bg-[#fafaf9] border-[#e5e5e5]'} ${isToday ? 'ring-2 ring-[#ff697f]' : ''}`} data-id="element-637">
                        <div className="flex items-center justify-between" data-id="element-638">
                          <span className={`text-xs font-medium ${shift?.isOvertime ? 'text-rose-700' : 'text-[#062133]'}`} data-id="element-639">
                            {day}
                          </span>
                          {shift && <span className={`p-0.5 rounded ${shift.isNightShift ? 'bg-[#062133]/10' : 'bg-amber-100'}`} data-id="element-640">
                              {shift.isNightShift ? <Moon className="h-2.5 w-2.5 text-[#062133]" data-id="element-641" /> : <Sun className="h-2.5 w-2.5 text-amber-600" data-id="element-642" />}
                            </span>}
                        </div>
                        {shift && <div className="mt-0.5" data-id="element-643">
                            <p className={`text-[10px] font-medium truncate ${shift.isOvertime ? 'text-rose-700' : 'text-emerald-700'}`} data-id="element-644">
                              {shift.site.length > 10 ? shift.site.slice(0, 10) + '...' : shift.site}
                            </p>
                            {shift.isOvertime && <span className="inline-block px-1 py-0.5 bg-rose-500 text-white text-[8px] font-bold rounded mt-0.5" data-id="element-645">
                                OT
                              </span>}
                          </div>}
                      </div>;
              })}
                </div>

                {/* Legend - variant-specific */}
                <div className="flex flex-wrap items-center gap-4 mt-4 pt-3 border-t border-[#e5e5e5]" data-id="element-646">
                  <span className="text-xs font-medium text-[#5a7280] uppercase tracking-wider" data-id="element-647">
                    Legend:
                  </span>
                  <div className="flex items-center gap-1.5" data-id="element-648">
                    <div className="w-3 h-3 rounded bg-emerald-50 border border-emerald-200" data-id="element-649" />
                    <span className="text-xs text-[#5a7280]" data-id="element-650">
                      Normal (1-19)
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-651">
                    <div className="w-3 h-3 rounded bg-rose-100 border border-rose-300" data-id="element-652" />
                    <span className="text-xs text-[#5a7280]" data-id="element-653">
                      Overtime (20+)
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-654">
                    <Sun className="h-3 w-3 text-amber-600" data-id="element-655" />
                    <span className="text-xs text-[#5a7280]" data-id="element-656">Day</span>
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-657">
                    <Moon className="h-3 w-3 text-[#062133]" data-id="element-658" />
                    <span className="text-xs text-[#5a7280]" data-id="element-659">Night</span>
                  </div>
                </div>
              </div> /* List View */ : <div className="overflow-hidden rounded-xl border border-[#e5e5e5]" data-id="element-660">
                <table className="min-w-full divide-y divide-[#e5e5e5]" data-id="element-661">
                  <thead className="bg-[#fafaf9]" data-id="element-662">
                    <tr data-id="element-663">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-664">
                        #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-665">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-666">
                        Site
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-667">
                        Shift Time
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-668">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-669">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-[#e5e5e5]" data-id="element-670">
                    {shifts.map((shift, index) => <tr key={index} className={`${shift.isOvertime ? 'bg-rose-50' : ''} hover:bg-[#fafaf9] transition-colors`} data-id="element-671">
                        <td className="px-4 py-3 whitespace-nowrap" data-id="element-672">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${index >= 19 ? 'bg-rose-500 text-white' : 'bg-[#062133]/10 text-[#062133]'}`} data-id="element-673">
                            {index + 1}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap" data-id="element-674">
                          <div className="text-sm font-medium text-[#062133]" data-id="element-675">
                            {shift.date.getDate()}{' '}
                            {getMonthName(shift.date.getMonth()).slice(0, 3)}
                          </div>
                          <div className="text-xs text-[#5a7280]" data-id="element-676">
                            {getDayName(shift.date.getDay())}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap" data-id="element-677">
                          <div className="flex items-center text-sm text-[#062133]" data-id="element-678">
                            <Building className="mr-1.5 h-4 w-4 text-[#5a7280]" data-id="element-679" />
                            {shift.site}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap" data-id="element-680">
                          <div className="text-sm text-[#062133]" data-id="element-681">
                            {shift.startTime} - {shift.endTime}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap" data-id="element-682">
                          <div className="flex items-center gap-1.5" data-id="element-683">
                            {shift.isNightShift ? <>
                                <Moon className="h-4 w-4 text-[#062133]" data-id="element-684" />
                                <span className="text-sm text-[#5a7280]" data-id="element-685">
                                  Night
                                </span>
                              </> : <>
                                <Sun className="h-4 w-4 text-amber-600" data-id="element-686" />
                                <span className="text-sm text-[#5a7280]" data-id="element-687">
                                  Day
                                </span>
                              </>}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap" data-id="element-688">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${shift.isOvertime ? 'bg-rose-500 text-white' : 'bg-emerald-500 text-white'}`} data-id="element-689">
                            {shift.isOvertime ? 'Overtime' : 'Normal'}
                          </span>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>

          {/* Footer - clean white background */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-[#e5e5e5] flex-shrink-0 bg-white rounded-b-3xl" data-id="element-690">
            <div className="flex items-center gap-6" data-id="element-691">
              <div className="flex items-center gap-2" data-id="element-692">
                <Clock className="h-4 w-4 text-[#5a7280]" data-id="element-693" />
                <span className="text-sm text-[#5a7280]" data-id="element-694">Total Hours:</span>
                <span className="text-sm font-semibold text-[#062133]" data-id="element-695">
                  {totalHours} hrs
                </span>
              </div>
              {isOverThreshold && overtimeHours > 0 && <div className="flex items-center gap-2 px-3 py-1 bg-rose-100 rounded-full" data-id="element-696">
                  <AlertTriangle className="h-3.5 w-3.5 text-rose-600" data-id="element-697" />
                  <span className="text-sm font-semibold text-rose-600" data-id="element-698">
                    {overtimeHours} hrs overtime
                  </span>
                </div>}
              {!isOverThreshold && availableShifts > 0 && <div className="flex items-center gap-2 px-3 py-1 bg-sky-100 rounded-full" data-id="element-699">
                  <span className="text-sm font-semibold text-sky-600" data-id="element-700">
                    {availableShifts * 12} hrs capacity available
                  </span>
                </div>}
            </div>
            <button onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-[#5a7280] bg-white border border-[#e5e5e5] rounded-xl hover:bg-[#fafaf9] hover:text-[#062133] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#062133] transition-colors" data-id="element-701">
              Close
            </button>
          </div>
        </div>
      </div>
    </>;
}